/**
 * features/hr/StaffPicker.tsx
 * ------------------------------------------------------------------
 * A compact, modern employee picker shared by the HR forms (leave,
 * severance). Mirrors the academic search pattern: nothing is listed
 * until the user types a query or chooses a cohort — no permanent
 * scrolling list. Once an employee is picked, a tidy chip shows the
 * selection with a "Change" affordance.
 * ---------------------------------------------------------------- */
import { useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';
import { glassFieldClass } from '@/components/ui/glass';
import { staffFullName, STAFF_CATEGORY_LABELS, type HrStaff, type StaffCategory } from '@/lib/hr';

export default function StaffPicker({
  staff,
  value,
  onChange,
  activeOnly = false,
  placeholder = 'Search by name or staff #',
}: {
  staff: HrStaff[];
  value: string;
  onChange: (id: string) => void;
  activeOnly?: boolean;
  placeholder?: string;
}) {
  const [category, setCategory] = useState<'all' | StaffCategory>('all');
  const [query, setQuery] = useState('');

  const selected = staff.find((s) => s.id === value);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return staff
      .filter((s) => !activeOnly || s.status === 'active')
      .filter((s) => category === 'all' || s.category === category)
      .filter((s) => !q || staffFullName(s).toLowerCase().includes(q) || s.employeeNo.toLowerCase().includes(q))
      .sort((a, b) => staffFullName(a).localeCompare(staffFullName(b)))
      .slice(0, 60);
  }, [staff, category, query, activeOnly]);

  // Results reveal only once the user signals intent: a cohort or a query.
  const showList = !selected && (category !== 'all' || query.trim() !== '');

  return (
    <div>
      <div className="grid grid-cols-[10.5rem_1fr] gap-2">
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value as 'all' | StaffCategory)}
          className={glassFieldClass('py-2 text-sm')}
        >
          <option value="all">All categories</option>
          {(Object.keys(STAFF_CATEGORY_LABELS) as StaffCategory[]).map((c) => (
            <option key={c} value={c}>{STAFF_CATEGORY_LABELS[c]}</option>
          ))}
        </select>
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={placeholder}
            className={glassFieldClass('py-2 pl-8 pr-8 text-sm')}
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {selected && (
        <div className="mt-2 flex items-center justify-between gap-2 rounded-xl border border-[var(--accent-link,#2d5295)]/30 bg-[var(--accent-link,#2d5295)]/5 px-3 py-2">
          <span className="text-sm font-medium text-gray-800 dark:text-gray-100">
            {staffFullName(selected)} <span className="text-gray-400 font-normal">#{selected.employeeNo}</span>
          </span>
          <button
            type="button"
            onClick={() => { onChange(''); setQuery(''); }}
            className="text-xs font-medium text-[var(--accent-link,#2d5295)] hover:underline"
          >
            Change
          </button>
        </div>
      )}

      {showList && (
        <ul className="mt-2 max-h-56 overflow-y-auto rounded-xl border border-gray-100 dark:border-gray-700 divide-y divide-gray-50 dark:divide-gray-700/50 bg-white/60 dark:bg-gray-800/40">
          {results.length === 0 ? (
            <li className="px-3 py-2.5 text-sm text-gray-500 dark:text-gray-400">No staff match.</li>
          ) : (
            results.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => { onChange(s.id); setQuery(''); }}
                  className="w-full text-left flex items-center justify-between gap-2 px-3 py-2.5 text-sm hover:bg-[var(--accent-link,#2d5295)]/5 transition-colors"
                >
                  <span className="text-gray-800 dark:text-gray-100">
                    {staffFullName(s)} <span className="text-gray-400">#{s.employeeNo}</span>
                  </span>
                  <span className="text-xs text-gray-400 shrink-0">{STAFF_CATEGORY_LABELS[s.category]}</span>
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
